import { Injectable, OnDestroy } from '@angular/core';
import {
  AssetHierarchySearchDTO,
  AssetType,
  AssetTypeDTO,
  BaseResponseDTO,
  CurrentType,
  CurrentTypeDTO,
  DefaultService,
  DeploymentStatusDTO,
  FuelTank,
  FuelTankEntity,
  FuelTankSize,
  FuelTankSizeDTO,
  Generator,
  GeneratorInventoryReportResponse,
  GeneratorMobileSearch,
  GeneratorSearch,
  GeneratorUsageReportResponse,
  HierarchySearchPayload,
  LocationType,
  LocationTypeDTO,
  MaintenanceType,
  MaintenanceTypeDTO,
  ManufactureType,
  ManufactureTypeDTO,
  ModelType,
  ModelTypeDTO,
  RecoverRequest,
  RefuelRequest,
  StatesEntity,
  StatusType,
  StatusTypeDTO,
  TelemetryActionRequest,
  Trailer,
  TrailerEntity,
  TrailerNameIdDTO,
  VoltageType,
  VoltageTypeDTO
} from '@psnb/psnb-api';
import { map } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
@Injectable({
  providedIn: 'root'
})
export class GeneratorService implements OnDestroy {
  public showProgressBarSubject$: BehaviorSubject<boolean>;
  constructor(private generatorService: DefaultService, private http: HttpClient) {
    this.showProgressBarSubject$ = new BehaviorSubject<boolean>(false);
  }

  public advanceSearchByAssetID(assetData: string): Observable<GeneratorSearch[]> {
    return this.generatorService
      .advanceSearchByAssetId(assetData)
      .pipe(map((response: GeneratorSearch[]): GeneratorSearch[] => response));
  }

  public advanceSearchByAssetIDMobile(assetData: string, timeZone?: string): Observable<GeneratorMobileSearch[]> {
    return this.generatorService
      .advanceSearchByAssetIdMobile(assetData, timeZone)
      .pipe(map((response: GeneratorMobileSearch[]): GeneratorMobileSearch[] => response));
  }

  public advanceSearchByMakeMobile(make: string, timeZone?: string): Observable<GeneratorMobileSearch[]> {
    // DefaultService declares advanceSearchByMakeMobile as returning Observable<object>.
    return this.generatorService
      .advanceSearchByMakeMobile(make, timeZone)
      .pipe(map((response: object): GeneratorMobileSearch[] => response as GeneratorMobileSearch[]));
  }

  public advanceSearchByModelMobile(model: string, timeZone?: string): Observable<GeneratorMobileSearch[]> {
    return this.generatorService
      .advanceSearchByModelMobile(model, timeZone)
      .pipe(map((response: GeneratorMobileSearch[]): GeneratorMobileSearch[] => response));
  }

  public advanceSearchByNode(
    nodeId: number,
    nodeCommunityServed: string,
    node: string
  ): Observable<GeneratorSearch[]> {
    return this.generatorService
      .advanceSearchByNode(nodeId, nodeCommunityServed, node)
      .pipe(map((response: GeneratorSearch[]): GeneratorSearch[] => response));
  }

  public advanceSearchByNodeMobile(
    nodeId: number,
    nodeCommunityServed: string,
    node: string,
    timeZone?: string
  ): Observable<GeneratorMobileSearch[]> {
    return this.generatorService
      .advanceSearchByNodeMobile(nodeId, nodeCommunityServed, node, timeZone)
      .pipe(map((response: GeneratorMobileSearch[]): GeneratorMobileSearch[] => response));
  }

  public advanceSearchByNwtMake(nwtMake: string): Observable<GeneratorSearch[]> {
    // DefaultService declares advanceSearchByNwtMake as returning Observable<object>.
    return this.generatorService
      .advanceSearchByNwtMake(nwtMake)
      .pipe(map((response: object): GeneratorSearch[] => response as GeneratorSearch[]));
  }

  public advanceSearchByNwtModel(nwtModel: string): Observable<GeneratorSearch[]> {
    // DefaultService declares advanceSearchByNwtModel as returning Observable<object>.
    return this.generatorService
      .advanceSearchByNwtModel(nwtModel)
      .pipe(map((response: object): GeneratorSearch[] => response as GeneratorSearch[]));
  }

  public advanceSearchByNwtTeam(nwtTeam: string): Observable<GeneratorSearch[]> {
    return this.generatorService
      .advanceSearchByNwtTeam(nwtTeam)
      .pipe(map((response: GeneratorSearch[]): GeneratorSearch[] => response));
  }

  public advanceSearchByNwtTeamMobile(nwtTeam: string, timeZone?: string): Observable<GeneratorMobileSearch[]> {
    return this.generatorService
      .advanceSearchByNwtTeamMobile(nwtTeam, timeZone)
      .pipe(map((response: GeneratorMobileSearch[]): GeneratorMobileSearch[] => response));
  }

  public checkDeploymentStatus(powerSupplyId: number) {
    return this.generatorService.checkDeploymentStatus(powerSupplyId).pipe(
      map((deploymentResponse: DeploymentStatusDTO): DeploymentStatusDTO => deploymentResponse));
  }

  public createAssetType(assetTypeDetails: AssetType): Observable<BaseResponseDTO> {
    return this.generatorService.createAssetType(assetTypeDetails).pipe(
        map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public createCurrent(currentdetails: CurrentType): Observable<BaseResponseDTO> {
    return this.generatorService.createCurrentType(currentdetails).pipe(
        map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public createFuelTankSize(fuelTankSizeDetails: FuelTankSize): Observable<BaseResponseDTO> {
    return this.generatorService.createFuelTankSize(fuelTankSizeDetails).pipe(
        map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public createHierarchySearch(hierarchySearchParams: HierarchySearchPayload): Observable<AssetHierarchySearchDTO[]> {
    return this.generatorService.getAssetHierarchySearch(hierarchySearchParams).pipe(
      map((response: AssetHierarchySearchDTO[]): AssetHierarchySearchDTO[] => response));
  }

  public createLocationType(locationTypeDetails: LocationType): Observable<BaseResponseDTO> {
    return this.generatorService.createLocation(locationTypeDetails).pipe(
        map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public createMaintenanceType(maintenanceTypeDetails: MaintenanceType): Observable<BaseResponseDTO> {
    return this.generatorService.createMaintenance(maintenanceTypeDetails).pipe(
      map((response: BaseResponseDTO): BaseResponseDTO => response));
  }

  public createManufactureType(manufactureTypeDetails: ManufactureType): Observable<BaseResponseDTO> {
    return this.generatorService.createManufactureType(manufactureTypeDetails).pipe(
      map((response: BaseResponseDTO): BaseResponseDTO => response));
  }

  public createModelType(modelTypeDetails: ModelType): Observable<BaseResponseDTO> {
    return this.generatorService.createModelType(modelTypeDetails).pipe(
      map((response: BaseResponseDTO): BaseResponseDTO => response));
  }

  public createStatusType(statusTypeDetails: StatusType): Observable<BaseResponseDTO> {
    return this.generatorService.createStatusType(statusTypeDetails).pipe(
      map((response: BaseResponseDTO): BaseResponseDTO => response));
  }

  public createVoltageType(voltageTypeDetails: VoltageType): Observable<BaseResponseDTO> {
    return this.generatorService.createVoltage(voltageTypeDetails).pipe(
      map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public deleteAssetType(assetTypeId: number): Observable<BaseResponseDTO> {
    return this.generatorService.deleteAsset(assetTypeId).pipe(
        map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public deleteCurrent(currentId: number): Observable<BaseResponseDTO> {
    return this.generatorService.deleteCurrentType(currentId).pipe(
        map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public deleteFuelTankByFuelTankId(fueltankId: number): Observable<BaseResponseDTO> {
    return this.generatorService
      .deleteFuelTankByFuelTankId(fueltankId)
      .pipe(map((response: BaseResponseDTO): BaseResponseDTO => response)
      );
  }

  public deleteFuelTankSize(fuelTankSizeId: number): Observable<BaseResponseDTO> {
    return this.generatorService.deleteFuelTankSize(fuelTankSizeId).pipe(
        map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public deleteGeneratorByGeneratorId(generatorId: number): Observable<BaseResponseDTO> {
    return this.generatorService
      .deleteGeneratorByGeneratorId(generatorId)
      .pipe(map((response: BaseResponseDTO): BaseResponseDTO => response)
      );
  }

  public deleteLocationType(locationTypeId: number): Observable<BaseResponseDTO> {
    return this.generatorService.deleteLocation(locationTypeId).pipe(
        map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public deleteMaintenanceType(maintenanceTypeId: number): Observable<BaseResponseDTO> {
    return this.generatorService.deleteMaintenance(maintenanceTypeId).pipe(
      map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public deleteManufactureType(manufactureTypeId: number): Observable<BaseResponseDTO> {
    return this.generatorService.deleteManufacture(manufactureTypeId).pipe(
      map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public deleteModelType(modelTypeId: number): Observable<BaseResponseDTO> {
    return this.generatorService.deleteModel(modelTypeId).pipe(
      map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public deleteStatusType(statusTypeId: number): Observable<BaseResponseDTO> {
    return this.generatorService.deleteStatusType(statusTypeId).pipe(
      map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public deleteTrailerByTrailerId(trailerId: number): Observable<BaseResponseDTO> {
    return this.generatorService
      .deleteTrailerByTrailerId(trailerId)
      .pipe(map((response: BaseResponseDTO): BaseResponseDTO => response)
      );
  }

  public deleteVoltageType(voltageTypeId: number): Observable<BaseResponseDTO> {
    return this.generatorService.deleteVoltage(voltageTypeId).pipe(
      map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public deployGenerator(deployRequest: TelemetryActionRequest): Observable<BaseResponseDTO> {
    return this.generatorService.createDeployment(deployRequest).pipe(
      map((generatorDeploymentResponse: BaseResponseDTO): BaseResponseDTO => generatorDeploymentResponse));
  }

  public fuelTankSearchById(searchValue: string): Observable<FuelTankEntity[]> {
    return this.generatorService.fuelTankSearchById(searchValue).pipe(
      map((fuelTankResponse: FuelTankEntity[]): FuelTankEntity[] => fuelTankResponse));
  }

  public generatorSearchByAssetId(searchValue: string): Observable<GeneratorSearch[]> {
    return this.generatorService.searchGenerator(searchValue).pipe(
      map((generatorResponse: GeneratorSearch[]): GeneratorSearch[] => generatorResponse));
  }

  public generatorSearchBySerialNumber(serialNumber: string): Observable<GeneratorSearch[]> {
    return this.generatorService.searchGeneratorBySerialNumber(serialNumber).pipe(
      map((generatorSearchResponse: GeneratorSearch[]): GeneratorSearch[] => generatorSearchResponse));
  }

  public getAllLoanedNWTTeams(): Observable<string[]> {
    return this.generatorService.getAllLoanedNWTTeams().pipe(
      map((nwtTeamsResult: string[]): string[] => nwtTeamsResult));
  }

  public getAssetTypesService(): Observable<Array<AssetTypeDTO>> {
    return this.generatorService.getAllAssetTypes().pipe(
        map((response: Array<AssetTypeDTO>): AssetTypeDTO[] => response)
    );
  }

  public getAllTrailers(): Observable<Array<TrailerEntity>> {
    return this.generatorService.getAllTrailers().pipe(
      map((response: TrailerEntity[]): TrailerEntity[] => response));
  }

  public getCurrentTypesService(): Observable<CurrentTypeDTO[]> {
    return this.generatorService.getAllCurrentTypes().pipe(
        map((response: CurrentTypeDTO[]): CurrentTypeDTO[] => response)
    );
  }

  public getLocationTypesService(): Observable<LocationTypeDTO[]> {
    return this.generatorService.getAllLocationTypes().pipe(
        map((response: LocationTypeDTO[]): LocationTypeDTO[] => response)
    );
  }

  public getFuelTankSizesService(): Observable<FuelTankSizeDTO[]> {
    return this.generatorService.getAllFuelTankSizes().pipe(
        map((response: FuelTankSizeDTO[]): FuelTankSizeDTO[] => response)
    );
  }

  public getModelsByManufacturer(manufacturer: string): Observable<ModelTypeDTO[]> {
    return this.generatorService.getModelsByManufacturer(manufacturer).pipe(
      map((response: ModelTypeDTO[]): ModelTypeDTO[] => response)
    );
  }

  public createFuelTank(fuelTankDetails): Observable<BaseResponseDTO> {
    return this.generatorService.createFuelTank(fuelTankDetails).pipe(map(
      (response: BaseResponseDTO): BaseResponseDTO => response
    ));
  }

  public getMakeOptions(prefix: string): Observable<string[]> {
    return this.generatorService.getMakeOptions(prefix).pipe(
      map((makeOptionsResponse: string[]): string[] => makeOptionsResponse));
  }

  public getManufactureTypesService(): Observable<ManufactureTypeDTO[]> {
    return this.generatorService.getAllManufactureTypes().pipe(map(
      (response: ManufactureTypeDTO[]): ManufactureTypeDTO[] => response
    ));
  }

  public getModelOptions(prefix: string): Observable<string[]> {
    return this.generatorService.getModelOptions(prefix).pipe(
      map((modelOptionsResponse: string[]): string[] => modelOptionsResponse));
  }

  public getModelTypesService(): Observable<ModelTypeDTO[]> {
    return this.generatorService.getAllModelTypes().pipe(map(
      (response: ModelTypeDTO[]): ModelTypeDTO[] => response
    ));
  }

  public getNwtTeamNameOptions(prefix: string): Observable<string[]> {
    return this.generatorService.getNwtTeamNameOptions(prefix).pipe(
      map((nwtTeamNameOptionsResponse: string[]): string[] => nwtTeamNameOptionsResponse));
  }

  public getStatesService(): Observable<StatesEntity[]> {
    return this.generatorService.getGeneratorStates().pipe(map(
      (statesResult: StatesEntity[]): StatesEntity[] => statesResult));
  }

  public getStatusTypesService(): Observable<StatusTypeDTO[]> {
    return this.generatorService.getAllStatusTypes().pipe(map(
      (response: StatusTypeDTO[]): StatusTypeDTO[] => response
    ));
  }

  public getTrailersByOwnerTeam(ownerTeam?: string): Observable<TrailerNameIdDTO[]> {
    return this.generatorService.getTrailersByOwnerTeam(ownerTeam).pipe(
      map((trailerResponse: TrailerNameIdDTO[]): TrailerNameIdDTO[] => trailerResponse));
  }

  public getVoltageTypesService(): Observable<VoltageTypeDTO[]> {
    return this.generatorService.getAllVoltageTypes().pipe(map(
      (response: VoltageTypeDTO[]): VoltageTypeDTO[] => response
    ));
  }

  public getMaintenanceTypesService(): Observable<MaintenanceTypeDTO[]> {
    return this.generatorService.getAllMaintenanceTypes().pipe(map(
      (response: MaintenanceTypeDTO[]): MaintenanceTypeDTO[] => response
    ));
  }

  public createGenerator(generatorDetails): Observable<BaseResponseDTO> {
    return this.generatorService.createGenerator(generatorDetails).pipe(map(
      (response: BaseResponseDTO): BaseResponseDTO => response
    ));
  }

  public createTrailer(trailerDetails): Observable<BaseResponseDTO> {
    return this.generatorService.createTrailer(trailerDetails).pipe(map(
      (response: BaseResponseDTO): BaseResponseDTO => response
    ));
  }

  public getInventoryReport(
    division: string,
    region: string,
    nodeIds: string,
    includeFuelTanks: boolean,
    includeTrailers: boolean
  ): Observable<GeneratorInventoryReportResponse> {
    return this.generatorService
      .getInventoryReport(
        division,
        region,
        nodeIds,
        includeFuelTanks,
        includeTrailers
      )
      .pipe(
        map((inventoryResponse: GeneratorInventoryReportResponse): GeneratorInventoryReportResponse => inventoryResponse)
      );
  }

  public getInventoryReportExcel(
    division: string,
    region: string,
    nodeIds: string,
    includeFuelTanks: boolean,
    includeTrailers: boolean
  ): Observable<Blob> {
    return this.generatorService
      .getInventoryReportExcel(division, region, nodeIds, includeFuelTanks, includeTrailers)
      .pipe(map((inventoryExcelResponse: Blob): Blob => inventoryExcelResponse));
  }

  public getUsageReport(
    division: string,
    region: string,
    nodeIds: string,
    history: boolean
  ): Observable<GeneratorUsageReportResponse> {
    return this.generatorService
      .getUsageReport(division, region, nodeIds, history)
      .pipe(
        map(
          (usageResponse: GeneratorUsageReportResponse): GeneratorUsageReportResponse =>
            usageResponse
        )
      );
  }

  public getUsageReportExcel(
    division: string,
    region: string,
    nodeIds: string,
    history: boolean,
    timezone: string
  ): Observable<Blob> {
    return this.generatorService
      .getUsageReportExcel(division, region, nodeIds, history, timezone)
      .pipe(map((usageExcelResponse: Blob): Blob => usageExcelResponse));
  }

  public get showProgressBar$(): BehaviorSubject<boolean> {
    return this.showProgressBarSubject$;
  }

  public recoverGenerator(recoverRequest: RecoverRequest): Observable<BaseResponseDTO> {
    return this.generatorService.recoverGenerator(recoverRequest).pipe(
      map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public refuelGenerator(refuelRequest: RefuelRequest): Observable<BaseResponseDTO> {
    return this.generatorService.refuelGenerator(refuelRequest).pipe(
      map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public setShowProgressBar(value: boolean): void {
    this.showProgressBarSubject$.next(value);
  }

  public trailerSearchById(searchValue: string): Observable<TrailerEntity[]> {
    return this.generatorService.trailerSearchById(searchValue).pipe(
      map((response: TrailerEntity[]): TrailerEntity[] => response));
  }

  public updateAssetType(assetTypeId: number, assetTypeDetails: AssetTypeDTO): Observable<BaseResponseDTO> {
    return this.generatorService.updateAssetType(assetTypeId, assetTypeDetails).pipe(
        map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public updateCurrent(currentId: number, currentdetails: any): Observable<BaseResponseDTO> {
    return this.generatorService.updateCurrentType(currentId, currentdetails).pipe(
        map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public updateFuelTank(fuelTankId: number, fuelTankDetails: FuelTank): Observable<BaseResponseDTO> {
    return this.generatorService.updateFuelTank(fuelTankId, fuelTankDetails).pipe(
      map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public updateFuelTankSize(fuelTankSizeId: number, fuelTankSizeDetails: FuelTankSizeDTO): Observable<BaseResponseDTO> {
    return this.generatorService.updateFuelTankSize(fuelTankSizeId, fuelTankSizeDetails).pipe(
        map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public updateGenerator(generatorId: number, generatorDetails: Generator): Observable<BaseResponseDTO> {
    return this.generatorService.updateGenerator(generatorId, generatorDetails).pipe(
      map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public updateLocationType(locationTypeId: number, locationTypeDetails: LocationTypeDTO): Observable<BaseResponseDTO> {
    return this.generatorService.updateLocation(locationTypeId, locationTypeDetails).pipe(
        map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public updateMaintenanceType(maintenanceTypeId: number, maintenanceTypeDetails: MaintenanceType): Observable<BaseResponseDTO> {
    return this.generatorService.updateMaintenance(maintenanceTypeId, maintenanceTypeDetails).pipe(
      map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public updateManufactureType(manufactureTypeId: number, manufactureTypeDetails: ManufactureType): Observable<BaseResponseDTO> {
    return this.generatorService.updateManufacture(manufactureTypeId, manufactureTypeDetails).pipe(
      map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public updateModelType(modelTypeId: number, modelTypeDetails: ModelType): Observable<BaseResponseDTO> {
    return this.generatorService.updateModel(modelTypeId, modelTypeDetails).pipe(
      map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public updateStatusType(statusTypeId: number, statusTypeDetails: StatusType): Observable<BaseResponseDTO> {
    return this.generatorService.updateStatusType(statusTypeId, statusTypeDetails).pipe(
      map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public updateTrailer(trailerId: number, trailerDetails: Trailer): Observable<BaseResponseDTO> {
    return this.generatorService.updateTrailer(trailerId, trailerDetails).pipe(
      map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public updateVoltageType(voltageTypeId: number, voltageTypeDetails: VoltageType): Observable<BaseResponseDTO> {
    return this.generatorService.updateVoltage(voltageTypeId, voltageTypeDetails).pipe(
      map((response: BaseResponseDTO): BaseResponseDTO => response)
    );
  }

  public ngOnDestroy(): void {
    this.showProgressBarSubject$?.complete();
  }
}
